import React from "react";
import { Box, Typography, Chip, ButtonBase } from "@mui/material";
import { MilestoneProgressbar } from "../../components/milestone-progressbar/MilestoneProgressbar";
import styles from "./MyRewardsWidget.module.scss";
import { useGetRewardWidgetQuery } from "./services/GetRewardWidgetAPISlice";
import { useAppSelector } from "../../store/hooks";
import { useTranslation } from "react-i18next";
import { ArrowIcon } from "../../components/icon-components/ArrowIcon";
import { useNavigate } from "react-router-dom";
import { getPatientRoute } from "../../utility/functions/common-functions";
import DataLoader from "../../components/custom-data-loader/DataLoader";
import MyRewardsWidgetSkeleton from "./MyRewardsWidgetSkeleton";
import {
  DEFAULT_TIER,
  PRIMARY_GREEN,
  TIER_IMG_MAP,
} from "../../utility/constants/common-constants";

const MyRewardsWidget: React.FC = () => {
  const { t } = useTranslation();
  const {
    patientProfileInfo: { patientProfileId },
  } = useAppSelector((state) => state.patientProfileDetails);

  const patientPreferredLang = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );

  const navigate = useNavigate();

  const handleNavigateToRewardActivity = () => {
    navigate(getPatientRoute(patientProfileId, "/my-reward-activity"), {
      state: { selectedTabId: 4 },
    });
  };

  const {
    data: rewardsWidgetData,
    isLoading,
    error,
  } = useGetRewardWidgetQuery({
    patientProfileId,
    patientPreferredLang,
  });

  const currentPoints = rewardsWidgetData?.totalEarnedRewardPoints ?? 0;

  // Extract milestones from rewards (ignore rangeFrom = 0)
  const rewardMilestones =
    rewardsWidgetData?.rewards
      ?.filter((reward) => reward.rangeFrom !== 0)
      .map((reward) => reward.rangeFrom) ?? [];

  const optinBonus = rewardsWidgetData?.optinBonus;

  // Add optinBonus only if it's a valid number and not already in array
  const milestones = [...rewardMilestones];

  if (typeof optinBonus === "number" && !milestones.includes(optinBonus)) {
    milestones.push(optinBonus);
  }

  // Sort milestones after including the bonus
  milestones.sort((a, b) => a - b);

  const maxValue = milestones[milestones.length - 1] ?? 4000;

  const currentTierLongDescription = rewardsWidgetData?.rewards?.find(
    (r) =>
      r.tierClassificationId ===
      (rewardsWidgetData?.currentTierClassificationId ?? DEFAULT_TIER)
  )?.longDescription;

  return (
    <DataLoader
      isLoading={isLoading}
      error={error}
      data={rewardsWidgetData}
      loadingComponent={<MyRewardsWidgetSkeleton />}
      emptyComponent={<Typography>No data found.</Typography>}
      errorComponent={
        <Typography color="error">Error loading widget data.</Typography>
      }
    >
      {() => (
        <Box className={styles.myRewardsWidget}>
          <ButtonBase onClick={handleNavigateToRewardActivity}>
            <Typography variant="h3" mb={3}>
              {t("rewardsWidget.title")}{" "}
              <ArrowIcon
                width={20}
                height={20}
                style={{ transform: "rotate(180deg)" }}
              />
            </Typography>
          </ButtonBase>

          <Box className={styles.widgetContainer}>
            {/* Left side */}
            <Box className={styles.leftCircleWrapper}>
              <Box className={styles.backgroundCircle} />
              <Box className={styles.gemWrapper}>
                <Box
                  component="img"
                  src={
                    TIER_IMG_MAP[
                      rewardsWidgetData?.currentTierClassificationId ??
                        DEFAULT_TIER
                    ]
                  }
                  alt={rewardsWidgetData?.currentTierDisplayName ?? "Tier"}
                  className={styles.gemImage}
                />
              </Box>
            </Box>

            {/* Right side */}
            <Box className={styles.rightContent}>
              <Typography variant="bodySmall" color="secondary.main">
                {t("rewardsWidget.youAreA")}
              </Typography>
              <Typography variant="h3" mb={2}>
                {rewardsWidgetData?.currentTierDisplayName}
              </Typography>
              {currentTierLongDescription && (
                <Typography variant="body1" mb={2}>
                  {currentTierLongDescription}
                </Typography>
              )}

              <Box mb={{ md: 8, xs: 2 }}>
                <Box className={styles.pointsRow}>
                  <Typography variant="subtitle1" mr={1}>
                    {currentPoints}
                  </Typography>
                  <Typography variant="body2" textTransform={"lowercase"}>
                    {t("points")}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography
                    variant="body2"
                    color="secondary.main"
                    className={styles.nextTierMessage}
                  >
                    {rewardsWidgetData?.nextTierMessage}
                  </Typography>
                </Box>

                <MilestoneProgressbar
                  milestones={milestones}
                  currentValue={currentPoints}
                  maxValue={maxValue}
                />
              </Box>
            </Box>

            {/* Footer */}
            <Box
              className={styles.footerBar}
              onClick={handleNavigateToRewardActivity}
              sx={{ cursor: "pointer" }}
            >
              <Box className={styles.footerContent}>
                <Typography variant="body1" color="common.white">
                  {t("rewardsWidget.youHave")}
                </Typography>
                <Chip
                  label={rewardsWidgetData?.voucherEligibleTierCount ?? 0}
                  className={styles.rewardsChip}
                  sx={{
                    backgroundColor: "#FFE9D9",
                    color: PRIMARY_GREEN,
                    fontWeight: "bold",
                    fontSize: "16px",
                    mx: 0.5,
                  }}
                />
                <Typography variant="body1" color="common.white" mr={1}>
                  {t("rewardsWidget.rewardsAvailable")}
                </Typography>
                <ArrowIcon
                  width={16}
                  height={16}
                  style={{ transform: "rotate(180deg)" }}
                  fill="#fff"
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </DataLoader>
  );
};

export default MyRewardsWidget;
